import { subMonths } from 'date-fns';

import { ApiVersions } from '../api-versions';
import type { components } from '../generated/openapi';
import type { GetMembersOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export type GetMembersQueryParams = Omit<GetMembersOpenAPIQueryParams, 'filter' | 'expand' | 'sort'> & {
  expand?: Array<'totalCountWithoutSelfOrOwner' | 'customRoles'>;
  sort?: 'displayName' | '-displayName' | 'lastSeen' | '-lastSeen';
  filter?: {
    ids?: string[];
    email?: string[];
    query?: string;
    role?: string[];
    team?: string;
    noteam?: boolean;
    lastSeen?:
      | 'never'
      | 'no-data'
      | '30-days-ago'
      | '60-days-ago'
      | '90-days-ago'
      | '120-days-ago'
      | '180-days-ago'
      | '365-days-ago';
    accessCheck?: `${components['schemas']['ActionSpecifier']}:${string}`;
  };
};

/**
 * Without this, getMembers sent no LD-API-Version header, so every call inherited the
 * session-auth default of "latest" — internal v4 in prod today, but silently following
 * the latest-api-version flag, so a bump to v5 would move all 13 call sites at once.
 *
 * v4 changes two things about this endpoint: `limit` defaults to 20 (v0 returned every
 * member), and the `accessCheck` filter needs an `id` filter alongside it (behind
 * `enable-access-check-filter-on-get-members-v4`). Since session auth already resolves to
 * v4 in prod, both already apply — callers that need the v0 unpaginated shape override
 * this with an explicit `apiVersion`, and access-check callers search a page first and
 * re-query its ids with the check. So pinning V4 is a no-op against the current prod
 * response while making the contract explicit.
 */
export const apiVersion = ApiVersions.V4;

export function querySerializer(queryParams: GetMembersQueryParams) {
  const searchParams = new URLSearchParams();

  if (queryParams.limit !== undefined && queryParams.limit > 0) {
    searchParams.set('limit', queryParams.limit.toString());
  }

  if (queryParams.offset !== undefined && queryParams.offset > 0) {
    searchParams.set('offset', queryParams.offset.toString());
  }

  if (queryParams.filter) {
    const serializedFilter = serializeMemberListFilterParam(queryParams.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  if (queryParams.sort) {
    searchParams.set('sort', queryParams.sort);
  }

  if (queryParams.expand && queryParams.expand.length > 0) {
    searchParams.set('expand', queryParams.expand.join(','));
  }

  return searchParams.toString();
}

function serializeMemberListFilterParam(value: NonNullable<GetMembersQueryParams['filter']>) {
  const filter: string[] = [];

  if (value.query) {
    filter.push(`query:${encodeURIComponent(value.query)}`);
  }

  if (value.role) {
    const unique = Array.from(new Set(value.role));
    if (unique.length > 0) {
      filter.push(`role:${unique.join('|')}`);
    }
  }

  if (value.team) {
    filter.push(`team:${value.team}`);
  }

  if (typeof value.noteam === 'boolean') {
    filter.push(`noteam:${value.noteam}`);
  }

  if (value.lastSeen) {
    switch (value.lastSeen) {
      case 'never':
        filter.push('lastSeen:{"never":true}');
        break;
      case 'no-data':
        filter.push('lastSeen:{"noData":true}');
        break;
      default:
        filter.push(`lastSeen:{"before":${lastSeenDateToUnix(value.lastSeen)}}`);
    }
  }

  if (value?.ids?.length) {
    filter.push(`id:${value.ids.join('|')}`);
  }

  if (value?.email?.length) {
    filter.push(`email:${value.email.join('|')}`);
  }

  if (value.accessCheck) {
    filter.push(`accessCheck:${value.accessCheck}`);
  }

  return filter.join(',');
}

function lastSeenDateToUnix(lastSeenDate: string) {
  switch (lastSeenDate) {
    case '30-days-ago':
      return subMonths(new Date(), 1).getTime();
    case '60-days-ago':
      return subMonths(new Date(), 2).getTime();
    case '90-days-ago':
      return subMonths(new Date(), 3).getTime();
    case '120-days-ago':
      return subMonths(new Date(), 4).getTime();
    case '180-days-ago':
      return subMonths(new Date(), 6).getTime();
    case '365-days-ago':
      return subMonths(new Date(), 12).getTime();
    default:
      throw new Error(`Invalid last seen date: ${lastSeenDate}`);
  }
}
